<template>
  <v-row class="bbr-programs--next-recommended-program-select" dense>
    <v-col cols="12" :sm="hasLevels ? 8 : 12">
      <v-autocomplete
        v-model="packageId"
        label="Next Recommended Program"
        item-text="name"
        item-value="package_id"
        :items="plans"
        :loading="loading"
        :error-messages="errorMessages"
        :hint="unavailableHint"
        :persistent-hint="!!unavailableHint"
        clearable
        outlined
        flat
        @change="selectPlan"
      />
    </v-col>

    <v-col v-if="hasLevels" cols="12" sm="4">
      <v-select
        :value="value"
        label="Level"
        item-text="level"
        item-value="id"
        :items="selectedPlan.levels"
        outlined
        flat
        @change="$emit('input', $event)"
      />
    </v-col>
  </v-row>
</template>

<script>
import Program from '@/models/Program'
import { find, groupBy, map, sortBy } from 'lodash'

export default {
  name: 'NextRecommendedProgramSelect',

  props: {
    // selected program (level) id
    value: {
      type: Number,
      default: null,
    },

    // program being edited, cannot recommend itself
    excludeId: {
      type: Number,
      default: null,
    },

    errorMessages: {
      type: [String, Array],
      default: () => [],
    },
  },

  data() {
    return {
      plans: [],
      packageId: null,
      loading: false,
    }
  },

  computed: {
    selectedPlan() {
      return find(this.plans, { package_id: this.packageId })
    },

    hasLevels() {
      return !!this.selectedPlan && this.selectedPlan.levels.length > 1
    },

    unavailableHint() {
      return this.value && !this.loading && !this.planOf(this.value)
        ? 'The current recommendation is archived and no longer shown to members.'
        : null
    },
  },

  watch: {
    value() {
      this.syncPackage()
    },
  },

  created() {
    this.fetchPlans()
  },

  methods: {
    async fetchPlans() {
      this.loading = true

      try {
        const { data } = await Program.where('recommendation_option', 1)
          .orderBy('name')
          .params({ limit: 1000 })
          .get()

        const programs = data.filter((program) => program.id !== this.excludeId)

        this.plans = sortBy(
          map(groupBy(programs, 'package_id'), (levels) => ({
            package_id: levels[0].package_id,
            name: levels[0].package_name,
            levels: sortBy(levels, 'name').map((program) => ({
              id: program.id,
              level: this.levelName(program),
            })),
          })),
          (plan) => (plan.name || '').toLowerCase()
        )

        this.syncPackage()
      } finally {
        this.loading = false
      }
    },

    planOf(programId) {
      return find(this.plans, (plan) => !!find(plan.levels, { id: programId }))
    },

    syncPackage() {
      const plan = this.value ? this.planOf(this.value) : null
      this.packageId = plan ? plan.package_id : null
    },

    selectPlan(packageId) {
      const plan = find(this.plans, { package_id: packageId })

      // default to the first level
      this.$emit('input', plan ? plan.levels[0].id : null)
    },

    // same rule as the API's AvailableProgramsService::getLevelFromName
    levelName(program) {
      const needle = program.type === 1 ? 'level' : 'round'
      const words = (program.name || '')
        .trim()
        .toLowerCase()
        .split(' ')
      const index = words.indexOf(needle)

      return index !== -1 && words[index + 1]
        ? `${needle.charAt(0).toUpperCase()}${needle.slice(1)} ${
            words[index + 1]
          }`
        : program.name
    },
  },
}
</script>
